import { isManager } from "@/lib/access";
import { useSearchParams } from "react-router-dom";
import { ShareBoardDialog } from "@/components/obboard/ShareBoardDialog";
import { Share2, Eye } from "lucide-react";
import { FileLinks } from "@/components/PaymentFiles";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { fmtDate as formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronLeft, ChevronRight, CalendarClock, CheckCircle2, ExternalLink, MoveRight, ListChecks, X, Receipt } from "lucide-react";
import { toast } from "sonner";
import MoveDueDateDialog from "@/components/MoveDueDateDialog";
import MarkPaidDialog from "@/components/MarkPaidDialog";
import ObligationForm from "@/components/ObligationForm";
import BulkDoneDialog from "@/components/BulkDoneDialog";
import InvoiceRegister, { ExportButtons } from "@/components/InvoiceRegister";
import { InvoiceFields, invoicePayload } from "@/components/InvoiceFields";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtAmount } from "@/lib/format";

const STATUS = {
  done: { label: "Done", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  pending: { label: "Pending", cls: "bg-slate-50 text-slate-700 border-slate-200" },
  due_soon: { label: "Due soon", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  overdue: { label: "Overdue", cls: "bg-rose-50 text-rose-700 border-rose-300" },
};
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shiftMonth = (key, n) => { const [y, m] = key.split("-").map(Number); return monthKey(new Date(y, m - 1 + n, 1)); };
const monthLabel = (key) => { const [y, m] = key.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }); };

const InvoiceEditor = ({ cell, onSaved, testid }) => {
  const [v, setV] = useState({ billable: cell.billable ?? null, amount: cell.invoice_amount != null ? String(cell.invoice_amount) : "" });
  const save = async () => {
    if (v.billable === null) return toast.error("Choose To invoice or Not to invoice");
    try { await api.patch(`/payments/${cell.payment_id}/invoice`, invoicePayload(v)); toast.success("Billing saved"); onSaved(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="border-t border-slate-100 pt-2 space-y-2">
      <InvoiceFields value={v} onChange={setV} testid={`${testid}-invoice`} />
      <Button size="sm" data-testid={`${testid}-invoice-save`} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">Save billing</Button>
    </div>
  );
};

const billingLabel = (c) => (c.billable === true ? `To invoice${c.invoice_amount != null ? ` · ${fmtAmount(c.invoice_amount, "INR")}` : ""}` : c.billable === false ? "Not to invoice" : null);

const Chip = ({ cell }) => {
  const s = STATUS[cell.status];
  return (
    <>
      <div>{s.label}</div>
      <div className="mono font-normal opacity-80">{cell.status === "done" ? `on ${formatDate(cell.paid_on)}` : formatDate(cell.due_date)}{cell.extended && " · ext."}</div>
      {cell.status === "done" && billingLabel(cell) && <div className="text-[10px] font-normal">{billingLabel(cell)}</div>}
      {cell.attachments?.length > 0 && <div className="text-[10px] font-normal" data-testid={`cell-docs-${cell.obligation_id}`}>{cell.attachments.length} doc{cell.attachments.length > 1 ? "s" : ""} attached</div>}
    </>
  );
};

const Cell = ({ cell, onMarkDone, onOpen, onChanged, testid, selectMode, checked, onToggle, acc = "own" }) => {
  const [open, setOpen] = useState(false);
  if (!cell) return <td className="px-2 py-2 text-center text-slate-300">—</td>;
  const s = STATUS[cell.status];
  const base = `w-full min-w-[92px] border rounded px-2 py-1.5 text-[11px] font-medium transition-colors hover:brightness-95 ${s.cls}`;
  if (selectMode) {
    return (
      <td className="px-2 py-2 text-center">
        {cell.can_mark_done ? (
          <button data-testid={testid} onClick={onToggle} className={`${base} relative ${checked ? "ring-2 ring-blue-700" : ""}`}>
            <span data-testid={`${testid}-check`} data-state={checked ? "checked" : "unchecked"}
              className={`absolute top-1 left-1 w-3.5 h-3.5 rounded-sm border grid place-items-center ${checked ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-400"}`}>
              {checked && <CheckCircle2 className="w-3 h-3" />}
            </span>
            <Chip cell={cell} />
          </button>
        ) : <div data-testid={testid} className={`${base} opacity-60`}><Chip cell={cell} /></div>}
      </td>
    );
  }
  return (
    <td className="px-2 py-2 text-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid={testid} className={base}><Chip cell={cell} /></button>
        </PopoverTrigger>
        <PopoverContent className="w-72 text-sm space-y-2" data-testid={`${testid}-popover`}>
          <div className="font-semibold">{cell.entity}</div>
          <div className="text-xs text-slate-600">Due {formatDate(cell.due_date)}{cell.extended && <> (extended from {formatDate(cell.standard_due_date)})</>}</div>
          {cell.owner_name && <div className="text-xs text-slate-500">Owner: {cell.owner_name}</div>}
          {cell.status === "done" && <div className="text-xs text-emerald-700">Marked done on {formatDate(cell.paid_on)}</div>}
          <FileLinks files={cell.attachments} testid={testid} />
          <div className="flex gap-2 pt-1">
            {cell.can_mark_done && acc !== "view" && (
              <Button size="sm" data-testid={`${testid}-done`} onClick={() => { setOpen(false); onMarkDone(cell); }} className="gap-1 bg-blue-700 hover:bg-blue-800 text-white">
                <CheckCircle2 className="w-3.5 h-3.5" />Mark done
              </Button>
            )}
            {acc === "own" && <Button size="sm" variant="outline" data-testid={`${testid}-open`} onClick={() => { setOpen(false); onOpen(cell); }} className="gap-1"><ExternalLink className="w-3.5 h-3.5" />Open</Button>}
          </div>
          {cell.status === "done" && cell.payment_id && (acc === "own" || acc === "invoice") && <InvoiceEditor cell={cell} testid={testid} onSaved={() => { setOpen(false); onChanged(); }} />}
        </PopoverContent>
      </Popover>
    </td>
  );
};

const ColumnHead = ({ col, canMove, onMove, idx, selectMode, colState, onToggleCol }) => (
  <th className="px-2 py-2 text-center font-medium align-top min-w-[110px]" data-testid={`board-col-${idx}`}>
    {selectMode && colState !== null && (
      <div className="mb-1"><Checkbox data-testid={`board-col-select-${idx}`} checked={colState} onCheckedChange={onToggleCol} /></div>
    )}
    <div className="text-xs text-zinc-950">{col.name}</div>
    <div className="mono text-[10px] text-slate-500 font-normal">{formatDate(col.standard_due_date)}</div>
    {col.extended_to.length > 0 && <div className="text-[10px] text-amber-700 font-normal">→ {col.extended_to.map(formatDate).join(", ")}</div>}
    {canMove && !selectMode && (
      <button data-testid={`board-move-${idx}`} onClick={() => onMove(col)} className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-blue-700 underline underline-offset-2">
        <MoveRight className="w-3 h-3" />Move due date
      </button>
    )}
  </th>
);

const Filters = ({ month, setMonth, group, setGroup, groups, owner, setOwner, members, isAdmin }) => (
  <div className="flex flex-wrap items-center gap-3">
    <div className="flex items-center border border-slate-200 rounded-md bg-white">
      <Button variant="ghost" size="icon" data-testid="board-prev-month" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft className="w-4 h-4" /></Button>
      <div className="px-2 text-sm font-medium min-w-[130px] text-center" data-testid="board-month">{monthLabel(month)}</div>
      <Button variant="ghost" size="icon" data-testid="board-next-month" onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight className="w-4 h-4" /></Button>
    </div>
    <Select value={group} onValueChange={setGroup}>
      <SelectTrigger data-testid="board-group" className="w-56 bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All filings</SelectItem>
        {Object.entries(groups).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
    {isAdmin && (
      <Select value={owner} onValueChange={setOwner}>
        <SelectTrigger data-testid="board-owner" className="w-48 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All owners</SelectItem>
          {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
  </div>
);

export default function FilingBoard({ initialTab = "board" }) {
  const { user } = useAuth();
  const isAdmin = isManager(user);
  const [month, setMonth] = useState(monthKey(new Date()));
  const [group, setGroup] = useState("all");
  const [owner, setOwner] = useState("all");
  const [members, setMembers] = useState([]);
  const [data, setData] = useState(null);
  const [moveCol, setMoveCol] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked] = useState([]);
  const [bulkIds, setBulkIds] = useState([]);
  const [tab, setTab] = useState(initialTab);
  const [sp] = useSearchParams();
  const [sharedBy, setSharedBy] = useState(sp.get("shared_by") || "");
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { api.get("/obligations-board/shared-with-me", { params: { board: "filing" } }).then((r) => setSharedWithMe(r.data)).catch(() => {}); }, []);
  const sharedOwner = sharedWithMe.find((s) => s.owner_id === sharedBy);
  const acc = sharedBy ? (data?.shared_access ?? null) : "own";
  const own = !sharedBy;

  const load = useCallback(async () => {
    try {
      const params = { month, ...(group !== "all" && { group }), ...(owner !== "all" && { owner_id: owner }), ...(sharedBy && { shared_by: sharedBy }) };
      setData((await api.get("/compliance/board", { params })).data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [month, group, owner, sharedBy]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/users/assignable?team=1").then((r) => setMembers(r.data)).catch(() => {}); }, [isAdmin]);

  const fetchOb = async (cell, setter) => {
    if (sharedBy && acc === "view") return;
    try { setter((await api.get(`/obligations/${cell.obligation_id}`)).data); } catch (e) { toast.error(formatErr(e)); }
  };
  const cellAt = (entity, key) => data?.cells.find((c) => c.entity === entity && c.key === key);
  const openIds = (pred) => (data?.cells || []).filter((c) => c.can_mark_done && pred(c)).map((c) => c.obligation_id);
  const groupState = (ids) => (ids.length === 0 ? null : ids.every((i) => picked.includes(i)));
  const toggleIds = (ids, on) => setPicked((p) => (on ? [...new Set([...p, ...ids])] : p.filter((i) => !ids.includes(i))));
  const toggleOne = (id) => setPicked((p) => (p.includes(id) ? p.filter((i) => i !== id) : [...p, id]));
  useEffect(() => { setPicked([]); }, [month, group, owner]);

  return (
    <div data-testid="filing-board-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Compliance</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Filing board</h1>
          <p className="text-sm text-slate-500 mt-1">Every client against the filings due this month. {acc === "view" ? "Click a cell to see its details." : "Click a cell to mark it done or open it."}</p>
        </div>
        {data && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="board-summary">
            {Object.entries(STATUS).map(([k, s]) => (
              <span key={k} className={`border rounded px-2 py-1 ${s.cls}`} data-testid={`board-summary-${k}`}>{s.label}: <b>{data.summary[k]}</b></span>
            ))}
            {data.summary.to_invoice !== undefined && <span className="border border-emerald-300 bg-white rounded px-2 py-1 inline-flex items-center gap-1" data-testid="board-summary-invoice">
              <Receipt className="w-3 h-3" />To invoice: <b>{data.summary.to_invoice}</b> · {fmtAmount(data.summary.to_invoice_total, "INR")}
            </span>}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Filters month={month} setMonth={setMonth} group={group} setGroup={setGroup} groups={data?.groups || {}}
          owner={owner} setOwner={setOwner} members={members} isAdmin={isAdmin && own} />
        {sharedWithMe.length > 0 && (
          <Select value={sharedBy || "own"} onValueChange={(v) => { setData(null); setSharedBy(v === "own" ? "" : v); setOwner("all"); setTab("board"); setSelectMode(false); }}>
            <SelectTrigger data-testid="board-shared-owner" className="w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="own">My board</SelectItem>
              {sharedWithMe.map((s) => <SelectItem key={s.owner_id} value={s.owner_id}>{s.owner_name}'s team board</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {isAdmin && own && <Button data-testid="board-share-btn" variant="outline" onClick={() => setShareOpen(true)} className="gap-2 bg-white ml-auto"><Share2 className="w-4 h-4 text-blue-700" />Share</Button>}
      </div>
      {sharedOwner && acc && (
        <div data-testid="board-shared-banner" className="mt-3 flex items-center gap-2 text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
          <Eye className="w-4 h-4" />You're viewing <b>{sharedOwner.owner_name}'s</b> team Filing board, shared with you.
          {{ view: " View only.", done: " You can mark filings done and attach acknowledgements.", invoice: " You can mark filings done and set invoicing." }[acc]}
        </div>
      )}
      <div className="mt-5 flex items-center gap-1 border-b border-slate-200">
        {[["board", "Board"], ...(own ? [["register", "Invoice register"]] : [])].map(([k, label]) => (
          <button key={k} data-testid={`board-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-blue-700 text-blue-700 font-semibold" : "border-transparent text-slate-500 hover:text-blue-700"}`}>{label}</button>
        ))}
      </div>
      {tab === "register" ? <InvoiceRegister month={month} owner={owner} onChanged={load} /> : (<>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {own && <Button size="sm" variant={selectMode ? "default" : "outline"} data-testid="board-select-toggle" onClick={() => { setSelectMode(!selectMode); setPicked([]); }}
          className={`gap-1 ${selectMode ? "bg-blue-700 text-white" : ""}`}>
          {selectMode ? <X className="w-3.5 h-3.5" /> : <ListChecks className="w-3.5 h-3.5" />}{selectMode ? "Exit select" : "Select to mark done"}
        </Button>}
        {selectMode && (
          <>
            <span className="text-xs text-slate-600" data-testid="board-selected-count">{picked.length} selected</span>
            <Button size="sm" variant="ghost" data-testid="board-select-all" onClick={() => toggleIds(openIds(() => true), true)}>Select all open</Button>
            <Button size="sm" data-testid="board-bulk-done" disabled={!picked.length} onClick={() => setBulkIds(picked)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />Mark {picked.length} done
            </Button>
          </>
        )}
        {own && <div className="ml-auto">
          <ExportButtons url="/compliance/board/export" name={`filing-board-${month}`} testid="board-export"
            params={{ month, ...(group !== "all" && { group }), ...(owner !== "all" && { owner_id: owner }) }} />
        </div>}
      </div>
      <div className="mt-5 bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="board-grid">
        {data && data.columns.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="board-empty">
            <CalendarClock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
            No compliance filings due in {monthLabel(month)}. Add them from Compliance register → “Add compliance calendar”.
          </div>
        ) : data && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium sticky left-0 bg-slate-50 min-w-[180px]">Client</th>
                {data.columns.map((c, i) => {
                  const ids = openIds((x) => x.key === c.key);
                  return <ColumnHead key={c.key} col={c} idx={i} onMove={setMoveCol} canMove={isAdmin && own && ids.length > 0} selectMode={selectMode}
                    colState={groupState(ids)} onToggleCol={(v) => toggleIds(ids, v === true)} />;
                })}
              </tr>
            </thead>
            <tbody>
              {data.entities.map((e, r) => (
                <tr key={e} className="border-b border-slate-100 last:border-0" data-testid={`board-row-${r}`}>
                  <td className="px-4 py-2 font-medium sticky left-0 bg-white">
                    <div className="flex items-center gap-2">
                      {selectMode && groupState(openIds((x) => x.entity === e)) !== null && (
                        <Checkbox data-testid={`board-row-select-${r}`} checked={groupState(openIds((x) => x.entity === e))}
                          onCheckedChange={(v) => toggleIds(openIds((x) => x.entity === e), v === true)} />
                      )}
                      {e}
                    </div>
                  </td>
                  {data.columns.map((c, i) => (
                    <Cell key={c.key} acc={acc} cell={cellAt(e, c.key)} testid={`board-cell-${r}-${i}`} selectMode={selectMode} onChanged={load}
                      checked={picked.includes(cellAt(e, c.key)?.obligation_id)} onToggle={() => toggleOne(cellAt(e, c.key).obligation_id)}
                      onMarkDone={(cell) => fetchOb(cell, setPayTarget)} onOpen={(cell) => fetchOb(cell, setEditTarget)} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>)}
      <BulkDoneDialog ids={bulkIds} onOpenChange={(v) => !v && setBulkIds([])} onDone={() => { setBulkIds([]); setPicked([]); setSelectMode(false); load(); }} />
      <MoveDueDateDialog column={moveCol} onOpenChange={(v) => !v && setMoveCol(null)} onDone={() => { setMoveCol(null); load(); }} />
      <MarkPaidDialog open={!!payTarget} onOpenChange={(v) => !v && setPayTarget(null)} obligation={payTarget} hideInvoice={acc === "done"} onDone={() => { setPayTarget(null); load(); }} />
      <ShareBoardDialog open={shareOpen} onClose={() => setShareOpen(false)} board="filing" />
      <ObligationForm open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)} editing={editTarget} onSaved={() => { setEditTarget(null); load(); }} />
    </div>
  );
}
